/**
 * Entry Authenticated Component
 *
 * Shown on the entry page when the user is already logged in.
 * Displays their email and a Continue button to proceed with onboarding.
 *
 * @see {@link ../entry.tsx} for the route that renders this component
 * @see {@link ./__tests__/entry-authenticated.test.tsx} for tests
 */

import { useNavigate } from 'react-router'
import { Button } from '@bnsn/ui/components/button'
import { ChevronRight } from 'lucide-react'
import { SlideScreen } from '../../premium-blueprints/components/slide-screen'
import { ONBOARD_COPY } from '../lib/constants'

interface EntryAuthenticatedProps {
  userEmail: string
}

/**
 * Authenticated user view for the entry page.
 * Shows account info and a Continue button.
 */
export const EntryAuthenticated = ({ userEmail }: EntryAuthenticatedProps): JSX.Element => {
  const navigate = useNavigate()

  const handleContinue = (): void => {
    navigate('/onboard/business-info')
  }

  return (
    <SlideScreen
      headline={ONBOARD_COPY.entrySubheading}
      showBack={false}
      showContinue={false}
      centerHeadline
      footer={
        <div className="flex items-center justify-end mt-8">
          <Button type="button" onClick={handleContinue}>
            Continue
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      }
    >
      <div>
        <img
          src="/images/jon-olya1b.webp"
          alt="Jon and Olya Benson"
          className="w-full md:w-1/2 md:float-left md:mr-6 mb-4 rounded-lg object-cover"
        />
        <p className="text-gray-600 whitespace-pre-line">{ONBOARD_COPY.entryBody}</p>
      </div>

      <div className="my-48 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3">
        <p className="text-base text-gray-900">
          You are logged in with <span className="font-bold">{userEmail}</span>.
        </p>
      </div>
    </SlideScreen>
  )
}
